import { CombinatorInput } from './CombinatorInput';
import { DuplicateButton } from './DuplicateButton';
import { EventCountFilter } from './EventCountFilter';
import { RemoveButton } from './RemoveButton';
import { ConditionRenderer } from '../condition/ConditionRenderer';
import { AddButton } from '../condition/AddButton';
import { EventSelect as EventSelectRenderer } from '../EventSelect';
import {
  useHasRequirementCombinatorError,
  useHasRequirementDataTypeError,
  useRequirementCombinatorState,
  useRequirementConditionsState,
  useRequirementCountFilterState,
  useRequirementDataTypeState,
} from '../../hooks/requirement';
import {
  ElementIndexContextProvider,
  ElementType,
  useVisualComponentsContext,
} from '../../contexts';
import { ClientDataType, Combinator, EditableCondition } from '../../types';
import { ConditionStateContextProvider } from '../../contexts/conditionState';
import { useRemoveAllConditions } from '../../hooks/condition';
import { useIsCatalogESIndex } from '../../hooks/useIsCatalogESIndex';
import { useCriteriaRequirementsState } from '../../hooks/criteria';

export const RequirementRenderer = () => {
  const { requirementCombinator = Combinator.AND, setRequirementCombinator } =
    useRequirementCombinatorState();
  const { requirementDataType = ClientDataType.User, setRequirementDataType } =
    useRequirementDataTypeState();
  const { setCountFilter, countFilter, countFilterType, setCountFilterType } =
    useRequirementCountFilterState();
  const { conditions } = useRequirementConditionsState();
  const { requirements } = useCriteriaRequirementsState();
  const removeAllConditions = useRemoveAllConditions();
  const hasCombinatorError = useHasRequirementCombinatorError(requirementCombinator);
  const hasDataTypeError = useHasRequirementDataTypeError(requirementDataType);
  const isCatalogESIndex = useIsCatalogESIndex();
  const isRemoveButtonEnabled = !isCatalogESIndex || requirements.length > 1;

  const { RequirementLayout } = useVisualComponentsContext();

  const handleDataTypeChange = (newDataType: ClientDataType) => {
    removeAllConditions();
    setRequirementDataType(newDataType);
  };

  const CombinatorSelect = (
    <CombinatorInput
      isInvalid={hasCombinatorError}
      onChange={(combinator) => {
        if (combinator) setRequirementCombinator(combinator);
      }}
      value={requirementCombinator}
    />
  );

  const CountFilter = (
    <EventCountFilter
      isInvalid={false} // TODO: update count filter to separate min/max error
      value={countFilter}
      countFilterType={countFilterType}
      onChange={setCountFilter}
      setCountFilterType={setCountFilterType}
    />
  );

  const EventSelect = (
    <EventSelectRenderer
      value={requirementDataType}
      onChange={(newDataType) => handleDataTypeChange(newDataType as ClientDataType)}
      isInvalid={hasDataTypeError}
      isInRequirement
    />
  );

  const Conditions = conditions.map((condition: EditableCondition, index: number) => (
    <ElementIndexContextProvider key={condition.id} index={index} type={ElementType.Condition}>
      <ConditionStateContextProvider key={condition.id}>
        <ConditionRenderer dataType={requirementDataType} />
      </ConditionStateContextProvider>
    </ElementIndexContextProvider>
  ));

  const Add = <AddButton />;

  const isDataTypeEvent = requirementDataType !== ClientDataType.User;

  return (
    <RequirementLayout
      Add={isDataTypeEvent && Add}
      CombinatorSelect={isDataTypeEvent && CombinatorSelect}
      Conditions={Conditions}
      CountFilter={isDataTypeEvent && CountFilter}
      dataType={requirementDataType}
      isCatalogESIndex={isCatalogESIndex}
      Delete={isRemoveButtonEnabled && <RemoveButton />}
      Duplicate={<DuplicateButton />}
      EventSelect={isDataTypeEvent && EventSelect}
      combinator={requirementCombinator}
    />
  );
};
